import React, { useState, useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, RefreshCw, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import PageInstructions from "@/components/PageInstructions";
import DisclaimerEstatistico from "@/components/legal/DisclaimerEstatistico";

const TURNOS_ORDER = ['PPT', 'PTM', 'PT', 'PTV', 'PTN', 'COR'];

interface ResultRow {
  data: string;
  turno: string;
  posicao: number;
  milhar: string;
  grupo: number;
}

const animalNames: Record<number, string> = {
  1: 'Avestruz', 2: 'Águia', 3: 'Burro', 4: 'Borboleta', 5: 'Cachorro',
  6: 'Cabra', 7: 'Carneiro', 8: 'Camelo', 9: 'Cobra', 10: 'Coelho',
  11: 'Cavalo', 12: 'Elefante', 13: 'Galo', 14: 'Gato', 15: 'Jacaré',
  16: 'Leão', 17: 'Macaco', 18: 'Porco', 19: 'Pavão', 20: 'Peru',
  21: 'Touro', 22: 'Tigre', 23: 'Urso', 24: 'Veado', 25: 'Vaca'
};

const ResultadosBicho: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [todayData, setTodayData] = useState<any>(null);
  const [previousData, setPreviousData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchResults = async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) setRefreshing(true);
      else setLoading(true);

      // Try edge function first (scrapes live data)
      let todayFromFn: any = null;
      let previousFromFn: any = null;
      try {
        const { data, error: fnError } = await supabase.functions.invoke('fetch-deu-no-poste', {
          body: null,
        });
        if (!fnError && data?.success) {
          todayFromFn = data.today;
          previousFromFn = data.previous;
        }
      } catch (e) {
        console.warn('Edge function falhou, usando dados do banco:', e);
      }

      // Always fetch from DB as fallback/complement
      const { data: dbRows } = await supabase
        .from('deu_no_poste_resultados')
        .select('*')
        .order('data', { ascending: false })
        .order('turno')
        .order('posicao')
        .limit(200);

      if (dbRows && dbRows.length > 0) {
        // Get the most recent date as "today"
        const latestDate = dbRows[0].data;
        
        // Build today turnos from DB if edge function didn't return today data
        const hasTodayFromFn = todayFromFn?.turnos && 
          TURNOS_ORDER.some(t => todayFromFn.turnos[t]?.length > 0);

        if (!hasTodayFromFn) {
          const todayRows = dbRows.filter(r => r.data === latestDate);
          const turnos: Record<string, ResultRow[]> = {};
          for (const row of todayRows) {
            const turno = row.turno;
            if (!turnos[turno]) turnos[turno] = [];
            turnos[turno].push({
              data: row.data,
              turno: row.turno,
              posicao: row.posicao,
              milhar: row.milhar,
              grupo: row.grupo
            });
          }
          todayFromFn = { date: latestDate, turnos };
        }

        // Build previous from DB (all dates except the latest)
        if (!previousFromFn?.rows?.length) {
          const prevRows = dbRows
            .filter(r => r.data !== latestDate)
            .map(r => ({
              data: r.data,
              turno: r.turno,
              posicao: r.posicao,
              milhar: r.milhar,
              grupo: r.grupo
            }));
          previousFromFn = { rows: prevRows };
        }
      }

      setTodayData(todayFromFn);
      setPreviousData(previousFromFn);
      setError(null);

      if (showRefreshToast) {
        toast({ title: "Atualizado!", description: "Resultados atualizados com sucesso." });
      }
    } catch (err: any) {
      console.error('Erro:', err);
      setError('Não foi possível carregar os resultados. Tente novamente.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchResults(); }, []);

  const renderTodayTable = () => {
    if (!todayData?.turnos) return null;

    const maxPos = 7;
    const activeTurnos = TURNOS_ORDER.filter(t => todayData.turnos[t]?.length > 0);

    if (activeTurnos.length === 0) {
      return (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            Aguardando resultados de hoje...
          </CardContent>
        </Card>
      );
    }

    const todayDate = todayData.date 
      ? new Date(todayData.date + 'T12:00:00')
      : new Date();
    const dateStr = todayDate.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const capitalizedDate = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

    return (
      <Card className="border-yellow-300 bg-yellow-50">
        <CardHeader className="pb-2 text-center px-2 sm:px-6">
          <CardTitle className="text-xl sm:text-2xl font-bold text-yellow-900">Deu no Poste</CardTitle>
          <p className="text-sm sm:text-lg font-semibold text-yellow-800">{capitalizedDate}</p>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr>
                <th className="bg-yellow-200 border border-yellow-300 px-1 py-1.5 sm:px-3 sm:py-2 text-center font-bold text-yellow-900 w-6 sm:w-10 text-[11px] sm:text-sm"></th>
                {TURNOS_ORDER.map(t => (
                  <th key={t} className="bg-yellow-200 border border-yellow-300 px-0.5 py-1.5 sm:px-3 sm:py-2 text-center font-bold text-yellow-900 text-[11px] sm:text-sm">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxPos }, (_, i) => i + 1).map(pos => (
                <tr key={pos} className={pos % 2 === 0 ? 'bg-yellow-50' : 'bg-white'}>
                  <td className="border border-yellow-300 px-1 py-1.5 sm:px-3 sm:py-2 text-center font-bold text-yellow-900 text-[11px] sm:text-sm">{pos}</td>
                  {TURNOS_ORDER.map(turno => {
                    const result = todayData.turnos[turno]?.find((r: ResultRow) => r.posicao === pos);
                    return (
                      <td key={turno} className="border border-yellow-300 px-0.5 py-1.5 sm:px-3 sm:py-2 text-center font-mono font-bold text-yellow-900 text-[10.5px] sm:text-sm leading-tight tracking-tight">
                        {result ? `${result.milhar}-${result.grupo}` : '0000-0'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    );
  };

  const renderPreviousTable = () => {
    if (!previousData?.rows?.length) return null;

    // Group by date and turno
    const grouped: Record<string, Record<string, ResultRow[]>> = {};
    for (const row of previousData.rows) {
      if (!grouped[row.data]) grouped[row.data] = {};
      if (!grouped[row.data][row.turno]) grouped[row.data][row.turno] = [];
      grouped[row.data][row.turno].push(row);
    }

    const dates = Object.keys(grouped).sort().reverse();

    return (
      <Card className="border-yellow-300 bg-yellow-50">
        <CardHeader className="pb-2 px-2 sm:px-6">
          <CardTitle className="text-base sm:text-lg text-yellow-900">
            Resultados Anteriores ({dates.length} {dates.length === 1 ? 'dia' : 'dias'})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr>
                <th className="bg-yellow-200 border border-yellow-300 px-1 py-1.5 sm:px-3 sm:py-2 text-center font-bold text-yellow-900 text-[11px] sm:text-sm w-12 sm:w-20">Data</th>
                <th className="bg-yellow-200 border border-yellow-300 px-1 py-1.5 sm:px-3 sm:py-2 text-center font-bold text-yellow-900 text-[11px] sm:text-sm w-10 sm:w-16">Turno</th>
                {[1, 2, 3, 4, 5].map(p => (
                  <th key={p} className="bg-yellow-200 border border-yellow-300 px-0.5 py-1.5 sm:px-3 sm:py-2 text-center font-bold text-yellow-900 text-[11px] sm:text-sm">{p}º</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map(date => {
                const turnos = TURNOS_ORDER.filter(t => grouped[date][t]);
                return turnos.map((turno, idx) => {
                  const results = grouped[date][turno].sort((a, b) => a.posicao - b.posicao);
                  const [, m, d] = date.split('-');
                  return (
                    <tr key={`${date}-${turno}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-yellow-50'}>
                      <td className="border border-yellow-300 px-1 py-1.5 sm:px-3 sm:py-2 text-center font-medium text-yellow-900 text-[11px] sm:text-sm">
                        {idx === 0 ? `${d}/${m}` : ''}
                      </td>
                      <td className="border border-yellow-300 px-1 py-1.5 sm:px-3 sm:py-2 text-center font-bold text-yellow-900 text-[11px] sm:text-sm">{turno}</td>
                      {[1, 2, 3, 4, 5].map(pos => {
                        const r = results.find(x => x.posicao === pos);
                        return (
                          <td key={pos} className="border border-yellow-300 px-0.5 py-1.5 sm:px-3 sm:py-2 text-center font-mono text-yellow-900 text-[10.5px] sm:text-sm leading-tight tracking-tight">
                            {r?.milhar || '—'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <SEOHead
        title="Resultados Jogo do Bicho Hoje - Deu no Poste ao Vivo"
        description="Confira os resultados do Jogo do Bicho de hoje e dias anteriores. Deu no Poste atualizado: PTM, PT, PTV, PTN, Coruja e Federal. Resultados em tempo real do Rio de Janeiro."
        keywords="resultados jogo do bicho, deu no poste hoje, resultado deu no poste, resultado federal, jogo do bicho resultado de hoje, resultado bicho ao vivo, resultado ptm, resultado coruja, deu no poste rio de janeiro"
        canonical="/resultados-bicho"
      />
    <div className="min-h-screen bg-yellow-50">
    <div className="container mx-auto p-2 sm:p-4 space-y-4 sm:space-y-6 max-w-4xl">
      <PageInstructions
        title="📖 Como ver os Resultados"
        steps={[
          "Os resultados do dia aparecem automaticamente ao abrir a página",
          "Clique em 'Atualizar Resultados' para buscar os mais recentes",
          "Veja todos os turnos: PPT, PTM, PT, PTV, PTN e Coruja",
          "Role para baixo para ver os resultados dos dias anteriores"
        ]}
      />
      <Card className="border-yellow-300 bg-yellow-50">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl lg:text-3xl font-bold text-yellow-900">
            Deu no Poste
          </CardTitle>
          <p className="text-yellow-700">
            Fonte: ojogodobicho.com/deu_no_poste.htm • Rio de Janeiro
          </p>
          <div className="flex justify-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchResults(true)}
              disabled={refreshing}
            >
              {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Atualizar
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href="https://www.ojogodobicho.com/deu_no_poste.htm" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1" />
                Site Oficial
              </a>
            </Button>
          </div>
        </CardHeader>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="p-8 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Carregando resultados...</span>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="p-6 text-center text-destructive">
            <p>{error}</p>
            <Button onClick={() => fetchResults()} className="mt-4">Tentar Novamente</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {renderTodayTable()}
          {renderPreviousTable()}
        </>
      )}
      <DisclaimerEstatistico />
    </div>
    </div>
    </>
  );
};

export default ResultadosBicho;
